import type { ComponentType } from "react";
import {
  RiDashboardLine,
  RiBox3Line,
  RiStackLine,
  RiStore3Line,
  RiShoppingCart2Line,
  RiGroup3Line,
  RiBarChart2Line,
  RiLink,
  RiCalendarLine,
  RiCalendarScheduleLine,
  RiSettings4Line,
  RiFileTextLine,
  RiBankCardLine,
  RiUserAddLine,
  RiLineChartLine,
  RiTeamLine,
  RiMailLine,
  RiGlobalLine,
  RiNotification3Line,
} from "@remixicon/react";

export type NavIcon = ComponentType<{ className?: string }>;

export type NavItem = {
  name: string;
  href: string;
  icon: NavIcon;
  permission?: string;
  ownerOnly?: boolean;
  paidOnly?: boolean;
  bookingsOnly?: boolean;
  superadminOnly?: boolean;
  keywords?: string[];
};

export type NavSection = {
  label?: string;
  items: NavItem[];
};

export const dashboardNav: NavSection[] = [
  {
    items: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: RiDashboardLine,
        permission: "dashboard",
        keywords: ["home", "overview"],
      },
    ],
  },
  {
    label: "Sell",
    items: [
      {
        name: "Orders",
        href: "/dashboard/orders",
        icon: RiShoppingCart2Line,
        permission: "orders",
        keywords: ["sales", "fulfil", "ship"],
      },
      {
        name: "POS Mode",
        href: "/dashboard/pos",
        icon: RiStore3Line,
        permission: "pos",
        keywords: ["point of sale", "register", "walk-in"],
      },
      {
        name: "Invoices",
        href: "/dashboard/invoices",
        icon: RiFileTextLine,
        permission: "invoices",
        keywords: ["bill", "payment"],
      },
    ],
  },
  {
    label: "Catalog",
    items: [
      {
        name: "Products",
        href: "/dashboard/products",
        icon: RiBox3Line,
        permission: "products",
        keywords: ["items", "catalog", "categories"],
      },
      {
        name: "Inventory",
        href: "/dashboard/inventory",
        icon: RiStackLine,
        permission: "inventory",
        keywords: ["stock", "restock"],
      },
    ],
  },
  {
    label: "Bookings",
    items: [
      {
        name: "Slots",
        href: "/dashboard/booking-slots",
        icon: RiCalendarLine,
        permission: "bookings",
        bookingsOnly: true,
        keywords: ["services", "availability"],
      },
      {
        name: "Bookings",
        href: "/dashboard/bookings",
        icon: RiCalendarScheduleLine,
        permission: "bookings",
        bookingsOnly: true,
        keywords: ["appointments", "reservations"],
      },
    ],
  },
  {
    label: "Grow",
    items: [
      {
        name: "Customers",
        href: "/dashboard/customers",
        icon: RiGroup3Line,
        permission: "customers",
        keywords: ["clients", "buyers", "crm"],
      },
      {
        name: "Sales link",
        href: "/dashboard/sales-link",
        icon: RiLink,
        permission: "settings",
        keywords: ["linktree", "template", "builder", "storefront", "customization", "colors", "banner", "hero"],
      },
      {
        name: "Analytics",
        href: "/dashboard/analytics",
        icon: RiBarChart2Line,
        permission: "analytics",
        keywords: ["reports", "revenue", "insights", "smart"],
      },
    ],
  },
  {
    label: "Platform",
    items: [
      {
        name: "Onboarding",
        href: "/admin/onboarding",
        icon: RiUserAddLine,
        superadminOnly: true,
      },
      {
        name: "Platform Analytics",
        href: "/admin/platform-analytics",
        icon: RiLineChartLine,
        superadminOnly: true,
      },
      {
        name: "Users",
        href: "/admin/users",
        icon: RiTeamLine,
        superadminOnly: true,
      },
      {
        name: "Marketing",
        href: "/admin/marketing",
        icon: RiMailLine,
        superadminOnly: true,
      },
      {
        name: "Stores",
        href: "/admin/stores",
        icon: RiGlobalLine,
        superadminOnly: true,
      },
      {
        name: "Email automation",
        href: "/admin/email-automation",
        icon: RiNotification3Line,
        superadminOnly: true,
      },
    ],
  },
];

export const settingsNavItem: NavItem = {
  name: "Settings",
  href: "/dashboard/settings",
  icon: RiSettings4Line,
  keywords: ["preferences", "store details", "checkout", "profile"],
};

export const billingNavItem: NavItem = {
  name: "Billing",
  href: "/dashboard/settings#billing",
  icon: RiBankCardLine,
  ownerOnly: true,
  keywords: ["plan", "upgrade", "subscription", "settings"],
};

export type NavAccess = {
  hasPermission: (permission: string) => boolean;
  isOwner: boolean;
  isPaid: boolean;
  bookingsEnabled: boolean;
  isSuperadmin: boolean;
};

export function canSeeNavItem(item: NavItem, access: NavAccess): boolean {
  if (item.superadminOnly) return access.isSuperadmin;
  if (item.bookingsOnly && !access.bookingsEnabled) return false;
  if (item.ownerOnly && !access.isOwner) return false;
  if (item.paidOnly && !access.isPaid) return false;
  if (item.permission && !access.hasPermission(item.permission)) return false;
  return true;
}

export function visibleNavSections(access: NavAccess): NavSection[] {
  return dashboardNav
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canSeeNavItem(item, access)),
    }))
    .filter((section) => section.items.length > 0);
}

export function isNavItemActive(href: string, pathname: string): boolean {
  if (href === "/dashboard") return pathname === "/dashboard";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Every nav item (including settings and billing), used for breadcrumbs. */
export const allNavItems: NavItem[] = [
  ...dashboardNav.flatMap((section) => section.items),
  settingsNavItem,
  billingNavItem,
];
